import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useSession } from "@/hooks/auth-hooks";
import {
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";

export const Route = createFileRoute("/dashboard/usage")({
	component: UsagePage,
});

type BillingTransaction = {
	amountCents: number;
	createdAt: string;
	description: string | null;
	generationId: string | null;
	id: string;
	outputUrl: string | null;
	provider: string;
	stripeCheckoutSessionId: string | null;
	transactionType: string;
};

const theme = {
	accent: "#6B3FF2",
	muted: "#adaaaa",
	text: "#fcfcfc",
	surface: "#131313",
	border: "rgba(255,255,255,0.08)",
	borderSoft: "rgba(255,255,255,0.06)",
	rowFill: "#181818",
	accentSoft: "#6B3FF2",
};

function formatUSD(cents: number) {
	return `$${(Math.abs(cents) / 100).toFixed(4)}`;
}

function getTransactionLabel(t: BillingTransaction) {
	if (t.description) return t.description;
	return "Usage charge";
}

function getProviderLabel(provider: string) {
	if (provider === "runware") return "Runware";
	if (provider === "admin") return "Admin";
	return provider;
}

const PROVIDER_OPTIONS = ["all", "runware", "admin"] as const;
type ProviderFilter = (typeof PROVIDER_OPTIONS)[number];

function UsagePage() {
	const { user } = useSession();
	const [providerFilter, setProviderFilter] = useState<ProviderFilter>("all");

	const billingHistoryQuery = useQuery({
		enabled: !!user,
		queryKey: ["billing", "history"],
		queryFn: async () => {
			const res = await fetch("/api/mcp/billing-history?limit=100", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) throw new Error("Failed to fetch billing history");
			return res.json() as Promise<{ transactions: BillingTransaction[] }>;
		},
	});

	const allUsageTransactions = (
		billingHistoryQuery.data?.transactions ?? []
	).filter((t) => t.transactionType !== "credit");

	const transactions =
		providerFilter === "all"
			? allUsageTransactions
			: allUsageTransactions.filter((t) => t.provider === providerFilter);

	const totalSpentCents = allUsageTransactions.reduce(
		(sum, t) => sum + Math.abs(t.amountCents),
		0,
	);

	const uniqueProviders = new Set(allUsageTransactions.map((t) => t.provider)).size;

	return (
		<DashboardLayout>
			{/* Page header */}
			<div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
				<div>
					<p
						className="text-[11px] font-bold uppercase tracking-[0.1em] mb-2"
						style={{ fontFamily: "inherit", color: "#6B3FF2" }}
					>
						Dashboard
					</p>
					<h1
						className="text-5xl font-semibold tracking-tighter mb-2"
						style={{ fontFamily: "'Geist', -apple-system, sans-serif", color: theme.text }}
					>
						Usage
					</h1>
					<p
						className="flex items-center gap-2 text-sm"
						style={{ color: theme.muted }}
					>
						Total spent:{" "}
						<span
							className="font-semibold text-xl"
							style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#6B3FF2" }}
						>
							${(totalSpentCents / 100).toFixed(2)}
						</span>
					</p>
				</div>
				<div
					className="hidden lg:block h-px flex-1 mx-8 mb-4"
					style={{
						background:
							"linear-gradient(to right, rgba(234,40,4,0.3), transparent)",
					}}
				/>
			</div>

			{/* Summary stat strip */}
			<div
				className="grid grid-cols-2 sm:grid-cols-3 gap-px mb-8"
				style={{ backgroundColor: theme.border }}
			>
				{[
					{ label: "Total Spent", value: `$${(totalSpentCents / 100).toFixed(2)}`, color: "#6B3FF2" },
					{ label: "Transactions", value: String(allUsageTransactions.length), color: theme.text },
					{ label: "Providers Used", value: String(uniqueProviders), color: theme.text },
				].map((s) => (
					<div
						key={s.label}
						className="px-6 py-4"
						style={{ backgroundColor: theme.surface }}
					>
						<p
							className="text-[10px] uppercase tracking-wide font-bold mb-1"
							style={{ fontFamily: "inherit", color: theme.muted }}
						>
							{s.label}
						</p>
						<p
							className="text-2xl font-semibold"
							style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: s.color }}
						>
							{s.value}
						</p>
					</div>
				))}
			</div>

			{/* Filters */}
			<div className="flex items-center gap-3 mb-6">
				<span
					className="text-[10px] font-bold uppercase tracking-wide shrink-0"
					style={{ fontFamily: "inherit", color: theme.muted }}
				>
					Filter
				</span>
				{PROVIDER_OPTIONS.map((option) => (
					<button
						key={option}
						type="button"
						onClick={() => setProviderFilter(option)}
						aria-pressed={providerFilter === option}
						className="text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						style={{
							fontFamily: "inherit",
							backgroundColor: providerFilter === option ? "#6B3FF2" : "transparent",
							color: providerFilter === option ? "#ffffff" : theme.muted,
							border: `1px solid ${providerFilter === option ? "#6B3FF2" : theme.borderSoft}`,
						}}
					>
						{option === "all" ? "All" : getProviderLabel(option)}
					</button>
				))}
			</div>

			<div
				className="overflow-hidden"
				style={{
					backgroundColor: theme.surface,
					border: `1px solid ${theme.border}`,
					borderTop: "3px solid #6B3FF2",
				}}
			>
				{/* Table column headers */}
				<div
					className="hidden md:grid grid-cols-[1.4fr_0.9fr_0.7fr_auto] gap-4 px-5 py-3"
					style={{ borderBottom: `1px solid ${theme.borderSoft}`, backgroundColor: theme.rowFill }}
				>
					{["Description", "Provider", "Amount", ""].map((col) => (
						<span
							key={col}
							className="text-[10px] font-bold uppercase tracking-wide"
							style={{ fontFamily: "inherit", color: theme.muted }}
						>
							{col}
						</span>
					))}
				</div>

				{billingHistoryQuery.isLoading ? (
					<div className="p-8 text-sm" role="status" aria-live="polite" aria-busy="true" style={{ color: theme.muted }}>
						Loading usage...
					</div>
				) : transactions.length === 0 ? (
					<div className="flex flex-col items-start gap-2 p-10" style={{ color: theme.muted }}>
						<span
							className="material-symbols-outlined mb-2"
							aria-hidden="true"
							style={{ fontSize: "32px", color: "#6B3FF2" }}
						>
							bar_chart
						</span>
						<p className="text-sm font-bold" style={{ color: theme.text }}>No usage yet</p>
						<p className="text-sm">Usage charges will appear here as you generate images, video, and audio.</p>
					</div>
				) : (
					<div
						role="list"
						aria-label="Usage transactions"
						className="divide-y"
						style={{ borderColor: theme.borderSoft }}
					>
						{transactions.map((t) => (
							<div
								key={t.id}
								role="listitem"
								className="grid grid-cols-1 md:grid-cols-[1.4fr_0.9fr_0.7fr_auto] gap-4 px-5 py-4 items-center transition-colors"
								onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = theme.rowFill; }}
								onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "transparent"; }}
							>
								<div className="min-w-0">
									<p
										className="text-sm font-bold"
										style={{ color: theme.text }}
									>
										{getTransactionLabel(t)}
									</p>
									<p
										className="text-xs mt-1"
										style={{ color: theme.muted, fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" }}
									>
										{new Date(t.createdAt).toLocaleString("en-US", {
											month: "short",
											day: "numeric",
											year: "numeric",
											hour: "numeric",
											minute: "2-digit",
										})}
									</p>
								</div>
								<div className="flex items-center">
									<span
										className="text-[10px] uppercase tracking-[0.1em] font-bold px-2 py-1"
										style={{
											fontFamily: "inherit",
											backgroundColor: "rgba(234,40,4,0.08)",
											border: "1px solid rgba(234,40,4,0.25)",
											borderLeft: "2px solid #6B3FF2",
											color: theme.accentSoft,
										}}
									>
										{getProviderLabel(t.provider)}
									</span>
								</div>
								<div className="flex items-center">
									<span
										className="font-mono text-sm font-bold"
										style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: theme.text }}
									>
										-{formatUSD(t.amountCents)}
									</span>
								</div>
								<div className="flex items-center justify-end">
									{t.outputUrl ? (
										<a
											href={t.outputUrl}
											target="_blank"
											rel="noopener noreferrer"
											aria-label="View result (opens in new tab)"
											className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1 transition-colors"
											style={{
												fontFamily: "inherit",
												backgroundColor: "rgba(234,40,4,0.08)",
												color: theme.accent,
												border: "1px solid #6B3FF2",
											}}
										>
											<span
												className="material-symbols-outlined"
												aria-hidden="true"
												style={{ fontSize: "12px" }}
											>
												open_in_new
											</span>
											View
										</a>
									) : (
										<span className="w-[52px]" />
									)}
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</DashboardLayout>
	);
}
